// components/tabs/EmailsTab.jsx
import { useState, useEffect } from 'react';
import { Callout, LoadRow } from '../UI';
import { EMAIL_TYPES, STAGES, fmtMoney, today } from '../../utils/constants';

const CATEGORIES = ['Required Notices', 'Contractor Correspondence', 'Internal & Agency'];

// Static fallback union list (updated 01/08/2026)
const UNION_LIST_FALLBACK = [
  { unit: '1,3,4,11,14,15,17,20,21', name: 'SEIU Local 1000', email: 'bcontracts@seiu1000.org', phone: '(866) 471-7348', contact: 'Contracts Department', workTypes: 'Administrative, clerical, financial, data processing, custodial, health records, office services, social services, accounting' },
  { unit: '2', name: 'CASE', email: 'kregan@calattorneys.org', phone: '(916) 669-4200', contact: 'Katherine E. Regan, Director of Labor Relations', workTypes: 'Attorneys, administrative law judges, hearing officers, legal work' },
  { unit: '5', name: 'CAHP', email: 'cahpdirectorjakejohnson@yahoo.com', phone: '(916) 452-6751', contact: 'Jake Johnson, President', workTypes: 'Highway patrol, traffic enforcement, law enforcement' },
  { unit: '6', name: 'CCPOA', email: 'padma.linker@ccpoa.org', phone: '(916) 376-6060', contact: 'Padma Linker, Chief of Labor', workTypes: 'Corrections, custodial officers, correctional facilities' },
  { unit: '7', name: 'CSLEA', email: 'KClark@cslea.com', phone: '(916) 696-6023', contact: 'Kasey Christopher Clark, General Manager', workTypes: 'Statewide law enforcement, investigators, park rangers, wildlife officers' },
  { unit: '8', name: 'CAL FIRE Local 2881', email: 'pboctor@L2881.org', phone: '(916) 277-9886', contact: 'Peter Boctor, State Rank and File Director', workTypes: 'Fire protection, forestry, fire prevention' },
  { unit: '9', name: 'PECG', email: 'notices@pecg.org', phone: '(916) 446-0400', contact: 'Ted Toppin, Executive Director', workTypes: 'Professional engineers, civil, electrical, mechanical, structural engineering, project management' },
  { unit: '10', name: 'CAPS-UAW Local 1115', email: '', phone: '(805) 316-5108', contact: 'Jacqueline Tkac, State President', workTypes: 'Scientists, environmental scientists, research analysts, laboratory work, scientific analysis', portalUrl: 'https://capsscientists.org/ab119-data-submission-portal/' },
  { unit: '12/13', name: 'IUOE Locals 39 & 501', email: 'bjohnson@local39.org', phone: '(916) 928-0399', contact: 'Brandy Johnson, Director of Public Employees', workTypes: 'Operating engineers, stationary engineers, equipment operators, facilities maintenance' },
  { unit: '16', name: 'UAPD', email: 'AB119Reports@uapd.com', phone: '(916) 442-6977', contact: 'Nereyda Rivera, Senior Representative', workTypes: 'Physicians, dentists, medical services, clinical care' },
  { unit: '18', name: 'CAPT', email: 'sean@psychtechs.net', phone: '(916) 329-9140', contact: 'Sean Bedrosian, Consultant', workTypes: 'Psychiatric technicians, mental health support, developmental services' },
  { unit: '19', name: 'AFSCME', email: 'BU19Contracts@ca.afscme57.org', phone: '(916) 923-1860', contact: 'Juan Vazquez', workTypes: 'Healthcare support, hospital workers, patient care assistants' },
];

const CALHR_URL = 'https://www.calhr.ca.gov/about-calhr/divisions-programs/labor-relations/personal-services-contracts/personal-services-contracts-contact-list/';

const EMAIL_SYSTEMS = {
  union: (ctx, unionInfo, ccList) =>
    'You are a California state contracting specialist at Energy Safety. Draft a formal Union Award Notification per Government Code §19131.\n\n' +
    (unionInfo ? 'RECIPIENT: ' + unionInfo.name + ' (Unit ' + unionInfo.unit + ')\nContact: ' + unionInfo.contact + (unionInfo.email ? ' — ' + unionInfo.email : '') + '\n\n' : '') +
    (ccList && ccList.length > 0 ? 'CC: ' + ccList.join(', ') + '\n\n' : '') +
    'Required elements: agency letterhead reference (Energy Safety, Admin Services Division), TO/CC notation at top of letter with the union recipient and any CC contacts listed above, contract title, contractor selected, award amount, anticipated start date, statement that effective date is no earlier than 10 business days from this notification, contact info for questions, signature block. Formal CA government letter format. No markdown.',
  intent_award:    'You are a California state contracting specialist. Draft a formal Notice of Intent to Award to ALL bidders. Required: "Notice of Intent to Award" subject line, contract title and solicitation reference, intended awardee and proposed award amount, statement this is an INTENT not a final award, protest rights notice — bidders have 5 business days to file per SCM Vol 1 §6.10, instructions for filing a protest, contact info. Neutral formal tone. No markdown.',
  bid_rejection:   'You are a California state contracting specialist. Draft a formal Bid Rejection Notice to unsuccessful bidders. Required: thank the bidder, state their proposal was not selected, reference contract title, note another firm best met the evaluation criteria without revealing scores, offer a debrief upon request, contact info. Professional courteous tone. No markdown.',
  award:           'You are a California state contracting specialist. Draft a formal Notice of Award to the successful contractor. Required: congratulations, contract title, agreement number, award amount, contract term dates, next steps for execution — signatures required, DGS routing if applicable, no work until fully executed, contact info. Formal professional tone. No markdown.',
  execution_notice:'You are a California state contracting specialist. Draft a formal Notice of Contract Execution. Required: contract is fully executed and effective, agreement number, effective start and end dates, maximum contract amount, invoice submission instructions and billing contact, assigned Contract Manager contact, statement that no pre-execution work is reimbursable, first deliverable due date if known. Clear actionable tone. No markdown.',
  amendment:       'You are a California state contracting specialist. Draft a formal Contract Amendment Notification. Required: reference to original agreement number and title, amendment number and effective date, clear description of what is changing, updated contract maximum amount and/or end date, statement all other terms remain in force, instructions for both parties to execute before it takes effect, contact info. Precise legal tone. No markdown.',
  dgs:             'You are a California state contracting specialist. Draft a DGS/OLS routing cover letter per PCC §10295. Required: agency name and division, request for DGS/OLS approval, contract title and description, contractor name and award amount, contract term, procurement method and justification, contracting out justification per GC §19130, list of documents in the routing package, contact info. Formal government correspondence. No markdown.',
  requestor:       'You are a California state contracting specialist. Draft an internal award notification memo to the requesting division. Required: TO/FROM/DATE/RE header format, contractor selected and award amount, description of services, contract timeline, next steps and estimated work start date, requestor responsibilities for invoice approval and deliverable acceptance, Contract Manager contact info. Professional memo style. No markdown.',
  protest_response:'You are a California state contracting specialist. Draft a formal protest acknowledgment letter. Required: acknowledge protest received, reference contract title and solicitation, protest under review per SCM Vol 1 §6.10, contract execution suspended pending resolution, anticipated timeline, instructions for additional information, contact info. Do NOT make any determination on merits. Formal neutral tone. No markdown.',
};

export default function EmailsTab({ contract, updateC, s215, profile, apiKey, callAI, toast }) {
  const [loading, setLoading] = useState({});
  const [copied, setCopied] = useState({});
  const [unionFetching, setUnionFetching] = useState(false);
  const [unionRecommending, setUnionRecommending] = useState(false);
  const [showUnionSelector, setShowUnionSelector] = useState(false);

  const emailDrafts = contract.pennyDrafts?.emails || {};
  const unionData = contract.pennyDrafts?.unionData || {};
  // unionData: { list: [...], fetchedAt: '...', recommended: { unit, name, email, ... }, selectedUnit: '...' }

  const unionList = unionData.list || UNION_LIST_FALLBACK;
  const selectedUnion = unionData.selectedUnit
    ? unionList.find(u => u.unit === unionData.selectedUnit)
    : unionData.recommended || null;

  function updateUnionData(updates) {
    updateC({ pennyDrafts: { ...(contract.pennyDrafts || {}), unionData: { ...unionData, ...updates } } });
  }

  function setEmailDraft(type, text) {
    updateC({ pennyDrafts: { ...(contract.pennyDrafts || {}), emails: { ...emailDrafts, [type]: { text, draftedAt: today() } } } });
  }

  function clearDraft(type) {
    const updated = { ...emailDrafts };
    delete updated[type];
    updateC({ pennyDrafts: { ...(contract.pennyDrafts || {}), emails: updated } });
  }

  // Auto-fetch union list when at stage 10
  useEffect(() => {
    if (contract.stage === 10 && !unionData.fetchedAt) {
      fetchUnionList(true);
    }
  }, [contract.stage]); // eslint-disable-line

  async function fetchUnionList(silent = false) {
    if (!silent) setUnionFetching(true);
    try {
      const resp = await fetch('https://api.allorigins.win/get?url=' + encodeURIComponent(CALHR_URL));
      const json = await resp.json();
      const html = json.contents || '';

      // Parse union contacts from page content
      const parsed = parseUnionContacts(html);
      if (parsed.length > 0) {
        updateUnionData({ list: parsed, fetchedAt: today() });
        if (!silent) toast.success('Union contact list updated from CalHR (' + parsed.length + ' unions)');
      } else {
        // Fallback to static list
        updateUnionData({ list: UNION_LIST_FALLBACK, fetchedAt: today() });
        if (!silent) toast.info('Using cached union list — CalHR page could not be parsed');
      }
    } catch (e) {
      updateUnionData({ list: UNION_LIST_FALLBACK, fetchedAt: today() });
      if (!silent) toast.info('Using cached union list — ' + e.message);
    } finally {
      if (!silent) setUnionFetching(false);
    }
  }

  function parseUnionContacts(html) {
    // Extract emails from the page
    const emailRegex = /href="mailto:([^"]+)"/g;
    const emails = [];
    let m;
    while ((m = emailRegex.exec(html)) !== null) {
      emails.push(m[1]);
    }
    // If we got emails, update the fallback list with fresh emails
    if (emails.length > 3) {
      return UNION_LIST_FALLBACK.map((u, i) => ({
        ...u,
        email: emails[i] || u.email,
      }));
    }
    return [];
  }

  async function recommendUnion() {
    setUnionRecommending(true);
    try {
      const listText = unionList.map(u =>
        'Unit ' + u.unit + ' — ' + u.name + ': ' + u.workTypes
      ).join('\n');

      const sys = 'You are a California state contracting expert. Based on the contract details, identify which bargaining unit(s) should receive the GC 19131 union notification. The notification must go to the exclusive representative of the bargaining unit whose members would perform this work if done in-house by state employees.\n\nReturn ONLY a JSON object: {"unit": "unit number(s)", "name": "union name", "reasoning": "brief explanation under 50 words"}. No other text.';

      const msg = 'Contract: ' + (contract.title || 'TBD') +
        '\nCategory: ' + (contract.category || 'Professional Services') +
        '\nDescription: ' + (contract.description || 'No description') +
        '\nIT/Non-IT: ' + (contract.isIT === 'yes' ? 'IT Services' : 'Non-IT Services') +
        '\n\nAvailable bargaining units:\n' + listText;

      const raw = await callAI(apiKey, {
        system: sys, model: 'claude-sonnet-4-6', max_tokens: 200,
        messages: [{ role: 'user', content: msg }],
      });

      const clean = raw.replace(/```json|```/g, '').trim();
      const result = JSON.parse(clean);
      const matched = unionList.find(u => u.unit === result.unit || u.name === result.name);

      if (matched) {
        updateUnionData({ recommended: { ...matched, reasoning: result.reasoning }, selectedUnit: null });
        toast.success('Penny recommends: ' + matched.name + ' (Unit ' + matched.unit + ')');
      } else {
        toast.info('Could not match recommendation — please select manually');
      }
    } catch (e) {
      toast.error('Recommendation failed: ' + e.message);
    } finally {
      setUnionRecommending(false);
    }
  }

  const ctx = [
    'Contract Title: ' + (contract.title || '[TBD]'),
    'Agency: ' + (profile.agencyName || 'Office of Energy Infrastructure Safety') + ', ' + (profile.agencyDivision || 'Admin Services Division'),
    'Budget / Award Amount: ' + fmtMoney(s215?.maxAmount || contract.budget),
    'Agreement No.: ' + (s215?.agreementNum || '[TBD]'),
    'Contractor: ' + (s215?.contractorName || '[TBD]'),
    'Contractor Address: ' + [s215?.contractorAddress, s215?.contractorCity, s215?.contractorState, s215?.contractorZip].filter(Boolean).join(', '),
    'Contract Term: ' + (s215?.startDate || '[TBD]') + ' through ' + (s215?.endDate || '[TBD]'),
    'Procurement Method: ' + (contract.procMethod || '[TBD]'),
    'Contact: ' + (s215?.contactName || profile.contactName || '[TBD]'),
    'Contact Phone: ' + (s215?.contactPhone || profile.contactPhone || '[TBD]'),
    'Contact Email: ' + (s215?.contactEmail || profile.contactEmail || '[TBD]'),
    'Signatory: ' + (s215?.signatoryName || profile.signatoryName || '[TBD]'),
    'Description: ' + (contract.description || '[TBD]'),
    'IT/Non-IT: ' + (contract.isIT === 'yes' ? 'IT Services — SCM Vol 2' : 'Non-IT Services — SCM Vol 1'),
  ].join('\n');

  async function draftEmail(type) {
    setLoading(p => ({ ...p, [type]: true }));
    try {
      const sys = type === 'union'
        ? EMAIL_SYSTEMS.union(ctx, selectedUnion, buildUnionCcList())
        : EMAIL_SYSTEMS[type];
      if (!sys) throw new Error('Unknown email type');
      const text = await callAI(apiKey, {
        system: sys, model: 'claude-sonnet-4-6', max_tokens: 800,
        messages: [{ role: 'user', content: 'Draft the ' + EMAIL_TYPES[type]?.label + ' for this contract:\n\n' + ctx }],
      });
      setEmailDraft(type, text);
      toast.success(EMAIL_TYPES[type]?.label + ' drafted');
    } catch (e) { toast.error('Draft failed: ' + e.message); }
    finally { setLoading(p => ({ ...p, [type]: false })); }
  }

  function buildUnionCcList() {
    const ccs = [];
    if (profile.unionHrEmail) ccs.push(profile.unionHrEmail);
    if (profile.unionLrEmail) ccs.push(profile.unionLrEmail);
    if (profile.unionCcEmails) {
      profile.unionCcEmails.split(',').map(e => e.trim()).filter(Boolean).forEach(e => ccs.push(e));
    }
    return ccs;
  }

  function openInEmail(type) {
    const draft = emailDrafts[type];
    if (!draft?.text) return;
    const subject = EMAIL_TYPES[type]?.label + ' — ' + (contract.title || 'Contract') + (s215?.agreementNum ? ' (' + s215.agreementNum + ')' : '');
    if (type === 'union') {
      const to = selectedUnion?.email || '';
      const ccs = buildUnionCcList();
      const ccParam = ccs.length > 0 ? '&cc=' + encodeURIComponent(ccs.join(',')) : '';
      window.location.href = 'mailto:' + to + '?subject=' + encodeURIComponent(subject) + ccParam + '&body=' + encodeURIComponent(draft.text);
    } else {
      window.location.href = 'mailto:?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(draft.text);
    }
  }

  function copyText(type) {
    const draft = emailDrafts[type];
    if (!draft?.text) return;
    navigator.clipboard.writeText(draft.text);
    setCopied(p => ({ ...p, [type]: true }));
    setTimeout(() => setCopied(p => ({ ...p, [type]: false })), 2000);
    toast.success('Copied to clipboard');
  }

  return (
    <div>
      <Callout type="blue" style={{ marginBottom: 20 }}>
        Penny drafts all required procurement notifications. Drafts persist across sessions. Edit after generating, then open in your email client or copy to clipboard.
      </Callout>

      {/* ── SB/DVBE Solicitation Section — only for SB/DVBE Option ── */}
      {contract.procMethod === 'SB/DVBE Option' && (() => {
        const vendors = contract.pennyDrafts?.sbDvbeVendors || [];
        const selected = contract.pennyDrafts?.sbDvbeSelected || [];
        const selectedVendors = vendors.filter(v => selected.includes(v.name));
        const solDrafts = contract.pennyDrafts?.sbDvbeSolicitations || {};

        if (!selectedVendors.length) return (
          <div style={{ marginBottom: 20, padding: '14px 16px', borderRadius: 'var(--r-md)', background: 'var(--gold-50)', border: '1.5px solid var(--gold-200)', fontSize: 12.5, color: 'var(--gold-800)' }}>
            ✦ SB/DVBE Solicitation emails will appear here once you select vendors in the <strong>Penny tab → SB/DVBE Vendor Pool</strong> card.
          </div>
        );

        function setSolDraft(vendorName, text) {
          updateC({ pennyDrafts: { sbDvbeSolicitations: { ...solDrafts, [vendorName]: { text, draftedAt: today() } } } });
        }

        async function draftSolicitation(vendor) {
          setLoading(p => ({ ...p, ['sbdvbe_' + vendor.name]: true }));
          try {
            const sow = contract.pennyDrafts?.exAFinal || contract.pennyDrafts?.sow || '';
            const sys = 'You are a California state contracting specialist at Energy Safety. Draft a formal SB/DVBE solicitation email to a certified vendor. Required elements: professional greeting using vendor contact name if known, agency name and division, contract title, brief description of services needed (2-3 sentences from the SOW), budget range if appropriate, requested response format (quote/proposal), submission deadline placeholder [SUBMISSION DEADLINE], contact info for questions, professional closing. No markdown. Formal but approachable tone.';
            const text = await callAI(apiKey, {
              system: sys,
              model: 'claude-sonnet-4-6',
              max_tokens: 600,
              messages: [{ role: 'user', content: 'Draft a solicitation email to this vendor:\n\nVendor: ' + vendor.name + '\nContact: ' + (vendor.contact || '[Contact Name]') + '\nCertification: ' + (vendor.certType || 'SB/DVBE') + '\n\nContract context:\n' + ctx + (sow ? '\n\nSOW Summary (first 800 chars):\n' + sow.slice(0, 800) : '') }],
              tag: 'sbdvbe_solicitation',
              contract_id: contract.id,
            });
            setSolDraft(vendor.name, text);
          } catch (e) { toast.error('Draft failed: ' + e.message); }
          finally { setLoading(p => ({ ...p, ['sbdvbe_' + vendor.name]: false })); }
        }

        async function draftAllSolicitations() {
          for (const vendor of selectedVendors) {
            await draftSolicitation(vendor);
          }
          toast.success('All ' + selectedVendors.length + ' solicitation drafts generated');
        }

        return (
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--slate-400)', marginBottom: 12, paddingBottom: 6, borderBottom: '1px solid var(--slate-100)' }}>
              SB/DVBE Solicitation
            </div>
            <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 14px', borderRadius: 'var(--r-md)', background: 'var(--navy-50)', border: '1px solid var(--navy-200)' }}>
              <div style={{ fontSize: 12.5, color: 'var(--navy-700)' }}>
                <strong>{selectedVendors.length} vendor{selectedVendors.length !== 1 ? 's' : ''} selected</strong> — generate individual solicitation emails for each firm
              </div>
              <button className="btn btn-gold btn-sm" onClick={draftAllSolicitations} disabled={Object.keys(loading).some(k => k.startsWith('sbdvbe_') && loading[k])}>
                ✦ Draft All Solicitations
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {selectedVendors.map(vendor => {
                const draft = solDrafts[vendor.name];
                const isLoading = loading['sbdvbe_' + vendor.name];
                return (
                  <div key={vendor.name} className="card" style={{ borderLeft: '3px solid var(--gold-400)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: draft?.text || isLoading ? 14 : 0 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy-800)' }}>{vendor.name}</span>
                          {vendor.certType && <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 99, background: 'var(--teal-50)', color: 'var(--teal-700)', border: '1px solid var(--teal-200)' }}>{vendor.certType}</span>}
                          {draft?.draftedAt && <span style={{ fontSize: 10, color: 'var(--teal-600)' }}>✓ Drafted {draft.draftedAt}</span>}
                        </div>
                        <p style={{ fontSize: 12, color: 'var(--slate-500)', margin: 0 }}>
                          {[vendor.contact, vendor.email, vendor.phone].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                      <button className="btn btn-gold btn-sm" disabled={isLoading} onClick={() => draftSolicitation(vendor)} style={{ marginLeft: 16, flexShrink: 0 }}>
                        {isLoading ? <><div className="spin spin-sm" /> Drafting…</> : draft?.text ? '↺ Redraft' : '✦ Draft'}
                      </button>
                    </div>
                    {isLoading && <LoadRow msg={'Drafting solicitation for ' + vendor.name + '…'} />}
                    {draft?.text && !isLoading && (
                      <div>
                        <pre style={{ background: 'var(--slate-50)', border: '1px solid var(--slate-200)', borderRadius: 'var(--r-md)', padding: '14px 16px', fontSize: 12.5, fontFamily: 'inherit', whiteSpace: 'pre-wrap', color: 'var(--slate-700)', lineHeight: 1.75, maxHeight: 300, overflowY: 'auto', marginBottom: 10 }}>
                          {draft.text}
                        </pre>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button className="btn btn-gold btn-sm" onClick={() => {
                            const subject = 'Request for Quote — ' + (contract.title || 'Professional Services') + (s215?.agreementNum ? ' (' + s215.agreementNum + ')' : '');
                            window.location.href = 'mailto:' + encodeURIComponent(vendor.email || '') + '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(draft.text);
                          }}>📧 Open in Email</button>
                          <button className="btn btn-ghost btn-sm" onClick={() => { navigator.clipboard.writeText(draft.text); toast.success('Copied'); }}>📋 Copy</button>
                          <button className="btn btn-ghost btn-sm" style={{ color: 'var(--slate-400)' }} onClick={() => setSolDraft(vendor.name, null)}>Clear</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
        const types = Object.entries(EMAIL_TYPES).filter(([, meta]) => meta.category === category);
        return (
          <div key={category} style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--slate-400)', marginBottom: 12, paddingBottom: 6, borderBottom: '1px solid var(--slate-100)' }}>
              {category}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {types.map(([type, meta]) => {
                const draft = emailDrafts[type];
                const isLoading = loading[type];
                const stageLabel = meta.stage != null ? 'Stage ' + (meta.stage + 1) + ' — ' + STAGES[meta.stage]?.short : null;

                return (
                  <div key={type} className="card" style={{ borderLeft: '3px solid ' + (meta.urgent ? 'var(--red-400)' : 'var(--slate-200)') }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: (draft?.text || isLoading || type === 'union') ? 14 : 0 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                          <span style={{ fontSize: 16 }}>{meta.icon}</span>
                          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy-800)' }}>{meta.label}</span>
                          {meta.urgent && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: 'var(--red-50)', color: 'var(--red-600)' }}>Required</span>}
                          {stageLabel && <span style={{ fontSize: 10, color: 'var(--slate-400)' }}>{stageLabel}</span>}
                          {draft?.draftedAt && <span style={{ fontSize: 10, color: 'var(--teal-600)' }}>✓ Drafted {draft.draftedAt}</span>}
                        </div>
                        <p style={{ fontSize: 12.5, color: 'var(--slate-500)', margin: 0 }}>{meta.desc}</p>
                      </div>
                      <button className="btn btn-gold btn-sm" disabled={isLoading || (type === 'union' && !selectedUnion)} onClick={() => draftEmail(type)} style={{ marginLeft: 16, flexShrink: 0 }}>
                        {isLoading ? <><div className="spin spin-sm" /> Drafting…</> : draft?.text ? '↺ Redraft' : '✦ Draft'}
                      </button>
                    </div>

                    {/* Union selector panel */}
                    {type === 'union' && (
                      <div style={{ marginBottom: 14, padding: '12px 14px', background: 'var(--slate-50)', borderRadius: 'var(--r-md)', border: '1px solid var(--slate-200)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy-700)' }}>
                            Union Recipient
                            {unionData.fetchedAt && <span style={{ fontWeight: 400, color: 'var(--slate-400)', marginLeft: 8 }}>List updated {unionData.fetchedAt}</span>}
                          </div>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn btn-ghost btn-xs" disabled={unionFetching} onClick={() => fetchUnionList(false)}>
                              {unionFetching ? '…' : '↺ Refresh List'}
                            </button>
                            <button className="btn btn-gold btn-xs" disabled={unionRecommending} onClick={recommendUnion}>
                              {unionRecommending ? <><div className="spin spin-sm" /> Analyzing…</> : '✦ Penny Recommend'}
                            </button>
                          </div>
                        </div>

                        {/* Penny recommendation */}
                        {unionData.recommended && (
                          <div style={{ marginBottom: 10, padding: '8px 12px', background: 'var(--teal-50)', border: '1px solid var(--teal-200)', borderRadius: 'var(--r-md)', fontSize: 12.5 }}>
                            <div style={{ fontWeight: 700, color: 'var(--teal-700)', marginBottom: 3 }}>
                              ✦ Penny recommends: {unionData.recommended.name} (Unit {unionData.recommended.unit})
                            </div>
                            {unionData.recommended.reasoning && (
                              <div style={{ color: 'var(--teal-600)', fontSize: 11.5 }}>{unionData.recommended.reasoning}</div>
                            )}
                          </div>
                        )}

                        {/* Union selector */}
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <select
                            value={unionData.selectedUnit || (unionData.recommended?.unit) || ''}
                            onChange={e => updateUnionData({ selectedUnit: e.target.value || null })}
                            style={{ flex: 1, padding: '7px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13 }}
                          >
                            <option value="">— Select union manually —</option>
                            {unionList.map(u => (
                              <option key={u.unit} value={u.unit}>Unit {u.unit} — {u.name}</option>
                            ))}
                          </select>
                        </div>

                        {/* Selected union details */}
                        {selectedUnion && (
                          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--slate-600)', lineHeight: 1.7 }}>
                            <div><strong>To:</strong> {selectedUnion.contact}
                              {selectedUnion.email && <> · <a href={'mailto:' + selectedUnion.email} style={{ color: 'var(--blue-600)' }}>{selectedUnion.email}</a></>}
                              {selectedUnion.portalUrl && <> · <a href={selectedUnion.portalUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--blue-600)' }}>Submit via portal</a></>}
                              {' · ' + selectedUnion.phone}
                            </div>
                            {buildUnionCcList().length > 0 && (
                              <div style={{ marginTop: 4 }}>
                                <strong>CC:</strong> {buildUnionCcList().join(', ')}
                                <span style={{ fontSize: 10.5, color: 'var(--slate-400)', marginLeft: 6 }}>(from your profile — update in User Profile)</span>
                              </div>
                            )}
                            {buildUnionCcList().length === 0 && (
                              <div style={{ marginTop: 4, color: 'var(--orange-500)', fontSize: 11.5 }}>
                                ⚠ No CC contacts set — add your HR and Labor Relations contacts in User Profile
                              </div>
                            )}
                          </div>
                        )}

                        {!selectedUnion && (
                          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--orange-600)' }}>
                            ⚠ Click "✦ Penny Recommend" or select a union manually before drafting.
                          </div>
                        )}
                      </div>
                    )}

                    {isLoading && <LoadRow msg={'Drafting ' + meta.label + '…'} />}

                    {draft?.text && !isLoading && (
                      <div>
                        <pre style={{
                          background: 'var(--slate-50)', border: '1px solid var(--slate-200)', borderRadius: 'var(--r-md)',
                          padding: '14px 16px', fontSize: 12.5, fontFamily: 'inherit', whiteSpace: 'pre-wrap',
                          color: 'var(--slate-700)', lineHeight: 1.75, maxHeight: 300, overflowY: 'auto', marginBottom: 10,
                        }}>
                          {draft.text}
                        </pre>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button className="btn btn-gold btn-sm" onClick={() => openInEmail(type)}>📧 Open in Email</button>
                          <button className="btn btn-ghost btn-sm" onClick={() => copyText(type)}>{copied[type] ? '✓ Copied' : '📋 Copy'}</button>
                          <button className="btn btn-ghost btn-sm" style={{ color: 'var(--slate-400)' }} onClick={() => clearDraft(type)}>Clear</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
